import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/lib/supabase";
import {
  Users,
  CreditCard,
  Shield,
  Activity,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  XCircle,
} from "lucide-react";

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    totalUsers: 0,
    registeredUsers: 0,
    activeUsers: 0,
    totalCards: 0,
    activeCards: 0,
    kycPending: 0,
    kycApproved: 0,
    kycRejected: 0,
    recentActivity: [],
  });

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      // Fetch total accounts created
      const { count: totalAccounts } = await supabase
        .from("accounts")
        .select("*", { count: "exact", head: true });

      // Get registered users count from accounts table
      // auth.users is not accessible via REST API, use accounts as proxy
      const registeredUsers = totalAccounts || 0;

      // Fetch active users (users who have logged in within last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const { count: activeUsers } = await supabase
        .from("accounts")
        .select("*", { count: "exact", head: true })
        .gte("last_active_at", thirtyDaysAgo.toISOString());

      // Fetch card statistics
      const { data: cardStats } = await supabase
        .from("cards")
        .select("card_status");

      const totalCards = cardStats?.length || 0;
      const activeCards =
        cardStats?.filter((card) => card.card_status === "active").length || 0;

      // Fetch KYC statistics from accounts table
      const { data: kycStats } = await supabase
        .from("accounts")
        .select("kyc_status");

      const kycPending =
        kycStats?.filter(
          (acc) =>
            acc.kyc_status === "pending" || acc.kyc_status === "processing"
        ).length || 0;
      const kycApproved =
        kycStats?.filter((acc) => acc.kyc_status === "approved").length || 0;
      const kycRejected =
        kycStats?.filter((acc) => acc.kyc_status === "rejected").length || 0;

      // Fetch recent activity (last 10 activities)
      const { data: recentCards } = await supabase
        .from("cards")
        .select("*, accounts(email)")
        .order("created_at", { ascending: false })
        .limit(5);

      const { data: recentAccounts } = await supabase
        .from("accounts")
        .select(
          "email, kyc_status, kyc_submitted_at, kyc_approved_at, created_at"
        )
        .order("created_at", { ascending: false })
        .limit(5);

      // Process recent activity
      const activities = [];

      // Add recent card activities
      recentCards?.forEach((card, index) => {
        const timeAgo = getTimeAgo(card.created_at);
        if (card.card_status === "active") {
          activities.push({
            id: `card-${index}`,
            type: "card_activated",
            user: card.accounts?.email || "Unknown",
            time: timeAgo,
            timestamp: new Date(card.created_at),
          });
        }
      });

      // Add recent KYC activities
      recentAccounts?.forEach((account, index) => {
        if (account.kyc_approved_at) {
          activities.push({
            id: `kyc-approved-${index}`,
            type: "kyc_approved",
            user: account.email,
            time: getTimeAgo(account.kyc_approved_at),
            timestamp: new Date(account.kyc_approved_at),
          });
        } else if (account.kyc_submitted_at) {
          activities.push({
            id: `kyc-submitted-${index}`,
            type: "kyc_submitted",
            user: account.email,
            time: getTimeAgo(account.kyc_submitted_at),
            timestamp: new Date(account.kyc_submitted_at),
          });
        }
      });

      // Sort activities by timestamp and take top 4
      const sortedActivities = activities
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, 4)
        .map(({ timestamp, ...activity }) => activity);

      setStats({
        totalUsers: totalAccounts || 0,
        registeredUsers: registeredUsers || 0,
        activeUsers: activeUsers || 0,
        totalCards,
        activeCards,
        kycPending,
        kycApproved,
        kycRejected,
        recentActivity: sortedActivities,
      });
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
      // Set default values on error
      setStats({
        totalUsers: 0,
        registeredUsers: 0,
        activeUsers: 0,
        totalCards: 0,
        activeCards: 0,
        kycPending: 0,
        kycApproved: 0,
        kycRejected: 0,
        recentActivity: [],
      });
    }
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days} day${days > 1 ? "s" : ""} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    if (minutes > 0) return `${minutes} min${minutes > 1 ? "s" : ""} ago`;
    return "Just now";
  };

  const statsCards = [
    {
      title: "Total Users",
      value: `${stats.totalUsers} / ${stats.registeredUsers}`,
      subValue: `${stats.activeUsers} active`,
      icon: Users,
      color: "text-blue-600",
      bgColor: "bg-blue-100 dark:bg-blue-900/20",
    },
    {
      title: "Cards Issued",
      value: stats.totalCards,
      subValue: `${stats.activeCards} active`,
      icon: CreditCard,
      color: "text-green-600",
      bgColor: "bg-green-100 dark:bg-green-900/20",
    },
    {
      title: "KYC Status",
      value: stats.kycApproved,
      subValue: `${stats.kycPending} pending`,
      icon: Shield,
      color: "text-purple-600",
      bgColor: "bg-purple-100 dark:bg-purple-900/20",
    },
    {
      title: "System Status",
      value: "Online",
      subValue: "All services operational",
      icon: Activity,
      color: "text-green-600",
      bgColor: "bg-green-100 dark:bg-green-900/20",
    },
  ];

  const getActivityIcon = (type) => {
    switch (type) {
      case "user_signup":
        return <Users className="h-4 w-4" />;
      case "kyc_approved":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "kyc_rejected":
        return <XCircle className="h-4 w-4 text-red-600" />;
      case "kyc_submitted":
        return <AlertCircle className="h-4 w-4 text-yellow-600" />;
      case "card_activated":
        return <CreditCard className="h-4 w-4 text-blue-600" />;
      default:
        return <Activity className="h-4 w-4" />;
    }
  };

  const getActivityText = (activity) => {
    switch (activity.type) {
      case "user_signup":
        return `New user signed up: ${activity.user}`;
      case "kyc_approved":
        return `KYC approved for: ${activity.user}`;
      case "kyc_rejected":
        return `KYC rejected for: ${activity.user}`;
      case "kyc_submitted":
        return `KYC submitted by: ${activity.user}`;
      case "card_activated":
        return `Card activated by: ${activity.user}`;
      default:
        return `Activity by: ${activity.user}`;
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h2 className="text-2xl font-bold">Dashboard</h2>
        <p className="text-muted-foreground">
          Overview of your platform's performance
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {statsCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  {stat.title}
                </CardTitle>
                <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                  <Icon className={`h-4 w-4 ${stat.color}`} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {stat.subValue}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts and Activity */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* KYC Overview */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              KYC Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm">Approved</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{stats.kycApproved}</span>
                  {stats.kycApproved + stats.kycPending + stats.kycRejected >
                    0 && (
                    <Badge variant="success">
                      {Math.round(
                        (stats.kycApproved /
                          (stats.kycApproved +
                            stats.kycPending +
                            stats.kycRejected)) *
                          100
                      )}
                      %
                    </Badge>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-yellow-600" />
                  <span className="text-sm">Pending</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{stats.kycPending}</span>
                  {stats.kycApproved + stats.kycPending + stats.kycRejected >
                    0 && (
                    <Badge variant="warning">
                      {Math.round(
                        (stats.kycPending /
                          (stats.kycApproved +
                            stats.kycPending +
                            stats.kycRejected)) *
                          100
                      )}
                      %
                    </Badge>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-red-600" />
                  <span className="text-sm">Rejected</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{stats.kycRejected}</span>
                  {stats.kycApproved + stats.kycPending + stats.kycRejected >
                    0 && (
                    <Badge variant="destructive">
                      {Math.round(
                        (stats.kycRejected /
                          (stats.kycApproved +
                            stats.kycPending +
                            stats.kycRejected)) *
                          100
                      )}
                      %
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-start gap-3">
                  <div className="mt-0.5">{getActivityIcon(activity.type)}</div>
                  <div className="flex-1 space-y-1">
                    <p className="text-sm">{getActivityText(activity)}</p>
                    <p className="text-xs text-muted-foreground">
                      {activity.time}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <Button variant="outline" className="w-full mt-4">
              View All Activity
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      {/* <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-2 md:grid-cols-4">
            <Button variant="outline">Export User Data</Button>
            <Button variant="outline">Generate Reports</Button>
            <Button variant="outline">System Backup</Button>
            <Button variant="outline">Clear Cache</Button>
          </div>
        </CardContent>
      </Card> */}
    </div>
  );
}
